import {
  createSchemaPropertyAccessor,
  getRelationship,
  getRelationshipShapes,
  getShapeById,
  getShapeDependencies,
  isModelShape,
  LEGACY_API_VERSION,
  type ProjectSchemaJSON,
  type ShapeJSON,
  type ShapeMap
} from '@takeshape/schema';
import { value } from '@takeshape/util';
import { getApiVersion, getSchema } from 'app/schema';
import { getWorkflowList, getWorkflowStepOptions } from 'app/workflows/selectors';
import escapeRegExp from 'lodash/escapeRegExp';
import pick from 'lodash/pick';
import uniq from 'lodash/uniq';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import type { Suggestion } from '../common/utils/autocomplete.js';
import { CONTENT_SEARCH_NAME, FILTER_TYPES } from './constants.js';
import { type ContentSearchSlice, initialContentSearchState } from './reducer.js';
import type { ContentSearchState, SearchFilter, TaxonomySuggestion } from './types.js';

export const getDomainState = (state: ContentSearchSlice): Record<string, ContentSearchState> =>
  state[CONTENT_SEARCH_NAME];
export const getSearchName = (_state: unknown, { searchName }: { searchName: string }): string => searchName;

export const getSearchState = createSelector(
  getDomainState,
  getSearchName,
  (state, searchName): ContentSearchState => state[searchName] || initialContentSearchState
);

const getShapeIds = createSelector(getSearchState, (state) => state.shapeIds);

export const getFilteredShapes = createSelector(getSchema, getShapeIds, (projectSchema, shapeIds) => {
  return shapeIds.reduce((shapes: ShapeMap, shapeId) => {
    const shape = getShapeById(projectSchema, shapeId);
    return shape
      ? {
          ...shapes,
          [shape.name]: shape,
          ...pick(projectSchema.shapes, getShapeDependencies(projectSchema, shape))
        }
      : shapes;
  }, {});
});

export const hasRelatedTaxonomy = createCachedSelector(getSchema, getFilteredShapes, (projectSchema, shapes) =>
  Boolean(
    Object.values(shapes).find((shape) =>
      createSchemaPropertyAccessor(projectSchema, shape).findValue(([, schema]) => {
        const relationship = getRelationship(schema);
        if (relationship) {
          const shapes = getRelationshipShapes(projectSchema.shapes, relationship.refs);
          return Boolean(
            shapes.find((shape: ShapeJSON) => {
              return shape?.model?.type === 'taxonomy';
            })
          );
        }

        return false;
      })
    )
  )
)(getSearchName);

function getTaxonomyOptions(
  itemsById: Record<string, TaxonomySuggestion>,
  projectSchema: ProjectSchemaJSON,
  ids: string[]
): SearchFilter[] {
  return ids.map((id) => {
    const item = itemsById[id];
    const shape = projectSchema.shapes[item._shapeName];
    return {
      label: `${shape.title}: ${item.text}`,
      value: id,
      type: FILTER_TYPES.TAXONOMY
    };
  });
}

export const getTaxonomySuggestions = createCachedSelector(getSearchState, getSchema, (state, projectSchema) => {
  const { taxonomySuggestions, taxonomyItemsById } = state;
  return taxonomySuggestions ? getTaxonomyOptions(taxonomyItemsById, projectSchema, taxonomySuggestions) : [];
})(getSearchName);

export const isFirstTime = createCachedSelector(
  getSearchState,
  (state) => !state.terms && !state.filters?.length && !state.loading && !state.requested && !state.results?.length
)(getSearchName);

export const getWorkflowSearchOptions = createCachedSelector(
  getApiVersion,
  getFilteredShapes,
  getWorkflowList,
  getSearchState,
  (apiVersion, filteredShapes, workflows, searchState) => {
    if (apiVersion === LEGACY_API_VERSION) {
      return [];
    }

    const { filters } = searchState;
    const input = searchState.terms;
    const statusSelected = filters && filters.filter((f) => f.type === FILTER_TYPES.STATUS).length > 0;
    if (statusSelected) {
      return [];
    }

    const workflowNames = uniq(
      Object.values(filteredShapes)
        .filter(isModelShape)
        .map((shape) => shape.workflow ?? 'default')
    );

    let optionsFilter: (option: Suggestion) => boolean;
    if (input) {
      const inputRegex = RegExp(escapeRegExp(`${input}`), 'i');
      optionsFilter = (option) => inputRegex.test(option.label);
    } else {
      optionsFilter = value(true);
    }

    return workflowNames.flatMap((workflowName) => {
      const workflow = workflows.find((w) => w.name === workflowName);
      return workflow
        ? getWorkflowStepOptions(workflow, 'Status')
            .map((o) => ({ ...o, type: FILTER_TYPES.STATUS }))
            .filter(optionsFilter)
        : [];
    });
  }
)(getSearchName);

export const getSearchFilters = createCachedSelector(getSearchState, (state) => state.filters || [])(getSearchName);

export const getSearchSuggestions = createCachedSelector(
  getTaxonomySuggestions,
  getWorkflowSearchOptions,
  (taxonomySuggestions, workflowSuggestions) => [...taxonomySuggestions, ...workflowSuggestions]
)(getSearchName);
